var _Label, _Glyphicon;
import React, { useState, useCallback } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { CloseButton, Text, Glyphicon, AccessibilityUtilities, Link, Label } from 'reaxl';
import { DelayedImpressionObserver } from 'reaxl-analytics';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
function HeaderSlidingMenu(_ref) {
  let {
    navItems = [],
    sponsoredContent = [],
    getSponsoredContent,
    onClick,
    onSponsorContentReady = () => {},
    isSlidingMenuOpen,
    handleSlidingMenuClose
  } = _ref;
  const [openAccordionEventkey, setOpenAccordionEventkey] = useState(null);
  const handleDelayedImpression = useCallback(e => {
    sponsoredContent.forEach(sponsor => {
      if (sponsor.sponsoredText) {
        onSponsorContentReady(e, sponsor);
      }
    });
  }, [sponsoredContent, onSponsorContentReady]);
  const onClose = () => {
    handleSlidingMenuClose();
    setOpenAccordionEventkey(null); // always reset accordion to closed state
  };
  const toggleSlidingMenuAccordion = (event, eventkey, navItem) => {
    setOpenAccordionEventkey(prevKey => prevKey === eventkey ? null : eventkey);
    // Sends Analytics event for Sell or Trade-In click on Mobile mode
    if (navItem.text === 'Sell or Trade-In') {
      onClick(event, {
        linkContext: 'HeaderNav',
        text: navItem.text,
        onEventKey: 'sellOrTradeInDropdown'
      });
    }
  };
  const renderSlidingMenuAccordionLinks = subNavItem => {
    const handleAccordionLinkClick = e => {
      onClick(e, subNavItem);
    };
    return /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(Text, {
        componentClass: "div",
        className: "sliding-menu-accordion-title margin-bottom-3",
        size: 100,
        weight: "bold",
        children: subNavItem.text
      }), /*#__PURE__*/_jsx("ul", {
        className: "sliding-menu-accordion-links padding-bottom-4",
        children: subNavItem.items.map(item => /*#__PURE__*/_jsx("li", {
          className: "margin-bottom-4",
          children: /*#__PURE__*/_jsx(Link, {
            onClick: handleAccordionLinkClick,
            href: item.href,
            children: /*#__PURE__*/_jsx(Text, {
              transformCase: "capitalize",
              weight: "normal",
              size: 300,
              children: item.text
            })
          })
        }, item.eventkey))
      })]
    }, subNavItem.text);
  };
  const renderSlidingMenuLinks = (navItem, index) => {
    const {
      eventkey,
      href,
      text,
      glyph,
      glyphOverride,
      items,
      isBeta,
      isOffsite,
      target,
      rel
    } = navItem;
    const isSlidingMenu = true;
    const isLastItem = index === navItems.length - 1;
    const handleSlidingMenuLinkClick = e => {
      onClick(e, navItem);
    };

    // sub menu nav links
    if (items) {
      return /*#__PURE__*/_jsxs("li", {
        "data-cmp": "slidingMenuLink",
        className: "sliding-menu-accordion-panel",
        children: [/*#__PURE__*/_jsxs("button", {
          className: "display-flex align-items-center gap-3",
          onClick: event => toggleSlidingMenuAccordion(event, eventkey, navItem),
          "aria-expanded": openAccordionEventkey === eventkey,
          onKeyDown: () => AccessibilityUtilities.withKeyboardAccessibility(event => toggleSlidingMenuAccordion(event, eventkey, navItem)),
          type: "button",
          "data-cmp": "slidingMenuAccordionButton",
          children: [glyphOverride ? /*#__PURE__*/_jsx("img", {
            src: glyphOverride,
            alt: text,
            className: "text-size-500"
          }) : /*#__PURE__*/_jsx(Glyphicon, {
            glyph: glyph,
            className: "text-size-500"
          }), /*#__PURE__*/_jsx(Text, {
            transformCase: "capitalize",
            size: 400,
            weight: "normal",
            children: text
          }), /*#__PURE__*/_jsx(Glyphicon, {
            glyph: openAccordionEventkey === eventkey ? 'menu-up' : 'menu-down',
            className: "text-size-650 margin-left-auto accordion-panel-menu-icon"
          })]
        }), /*#__PURE__*/_jsx("div", {
          className: clsx(openAccordionEventkey === eventkey && 'open', 'sliding-menu-accordion-panel-content'),
          children: openAccordionEventkey === eventkey && /*#__PURE__*/_jsxs(DelayedImpressionObserver, {
            onChange: handleDelayedImpression,
            children: [items.map(subNavItem => renderSlidingMenuAccordionLinks(subNavItem)), sponsoredContent.filter(sponsor => sponsor.eventkey === eventkey).length > 0 && /*#__PURE__*/_jsx("div", {
              className: "margin-bottom-4 sliding-menu-sponsored-content",
              children: getSponsoredContent(sponsoredContent.filter(sponsor => sponsor.eventkey === eventkey), onClick, isSlidingMenu)
            })]
          }, "sponsored-content-delayed-impression")
        })]
      }, `${text}-${eventkey}`);
    }

    // top level header links
    return /*#__PURE__*/_jsx("li", {
      "data-cmp": "slidingMenuLink",
      className: clsx({
        'has-offsite-icon': isOffsite
      }),
      children: /*#__PURE__*/_jsxs(Link, {
        className: "display-flex align-items-center gap-3",
        href: href,
        onClick: handleSlidingMenuLinkClick
        // for accessibility - ensure the sliding menu is closed when user tabs out of its contents
        ,
        ...(isLastItem && {
          onBlur: onClose
        }),
        ...(target && {
          target
        }),
        ...(rel && {
          rel
        }),
        children: [glyphOverride ? /*#__PURE__*/_jsx("img", {
          src: glyphOverride,
          alt: text,
          className: "text-size-500"
        }) : /*#__PURE__*/_jsx(Glyphicon, {
          glyph: glyph,
          className: "text-size-500"
        }), /*#__PURE__*/_jsxs(Text, {
          transformCase: "capitalize",
          size: 400,
          weight: "normal",
          children: [text, isBeta && (_Label || (_Label = /*#__PURE__*/_jsx(Label, {
            bsStyle: "brand",
            className: "margin-left-2 beta-label",
            children: "Beta"
          })))]
        }), isOffsite && (_Glyphicon || (_Glyphicon = /*#__PURE__*/_jsx(Glyphicon, {
          glyph: "offsite",
          className: "margin-left-auto padding-right-1 text-size-500 offsite-icon"
        })))]
      })
    }, `${text}-${eventkey}`);
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("div", {
      "aria-hidden": "true",
      "data-cmp": "slidingMenuOverlay",
      className: clsx(isSlidingMenuOpen && 'open', 'sliding-menu-overlay'),
      onClick: onClose
    }), /*#__PURE__*/_jsxs("div", {
      "data-cmp": "slidingMenu",
      className: clsx(isSlidingMenuOpen && 'open', 'sliding-menu slide-from-left'),
      children: [/*#__PURE__*/_jsx("div", {
        className: "sliding-menu-header display-flex justify-content-end padding-4",
        children: /*#__PURE__*/_jsx(CloseButton, {
          onClick: onClose
        })
      }), /*#__PURE__*/_jsx("nav", {
        "data-cmp": "headerMobileNav",
        className: "sliding-menu-container margin-top-4 padding-horizontal-5",
        children: /*#__PURE__*/_jsx("ul", {
          className: "sliding-menu-links",
          children: navItems.map((navItem, index) => renderSlidingMenuLinks(navItem, index))
        })
      })]
    })]
  });
}
export default HeaderSlidingMenu;